import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import R from 'ramda';
import Duration from './Duration';
import PlayPauseControl from './PlayPauseControl';
import VolumeControl from './VolumeControl';
import Seekbar from './Seekbar';

export const generateSinglePlayButtonClassNames = ({ playButtonPosition }) => classNames(
  'video-player__controls-btn-icon-simple',
  {
    'video-player__controls-btn-icon-simple--top-left': playButtonPosition === 'topLeft',
    'video-player__controls-btn-icon-simple--top-right': playButtonPosition === 'topRight',
    'video-player__controls-btn-icon-simple--bottom-right': playButtonPosition === 'bottomRight',
    'video-player__controls-btn-icon-simple--bottom-left': playButtonPosition === 'bottomLeft',
    'video-player__controls-btn-icon-simple--middle': playButtonPosition === 'middle',
  },
);

const Controls = ({
  id,
  playing,
  muted,
  volume,
  played,
  videoDuration,
  setTogglePlayPause,
  setToggleMuted,
  setVolume,
  setSeeking,
  playerEl,
  seeking,
  controls,
  playIconSrc,
  pauseIconSrc,
  muteIconSrc,
  volumeIconSrc,
  playButtonPosition,
  showOnlyMuteControls,
  playButtonIconWidth,
  playButtonIconHeight,
}) => (
  <Fragment>
    {showOnlyMuteControls ? (
      <VolumeControl
        id={id}
        muted={muted}
        muteIconSrc={muteIconSrc}
        volumeIconSrc={volumeIconSrc}
        controls={controls}
        volume={volume}
        setToggleMuted={setToggleMuted}
        setVolume={setVolume}
        title={muted ? R.prop('unmute')(controls) : R.prop('mute')(controls)}
        extraClass="video-player__controls-volume-mute-only"
        showOnlyMuteControls={showOnlyMuteControls}
      />
    ) : (
      playing ?
      (
        <div className="video-player__controls grid-x grid-padding-x align-middle">
          <div className="cell auto shrink flex-container">
            <PlayPauseControl
              id={id}
              controls={controls}
              playing={playing}
              setTogglePlayPause={setTogglePlayPause}
              pauseIconSrc={pauseIconSrc}
              playIconSrc={playIconSrc}
              title={playing ? R.prop('pause')(controls) : R.prop('play')(controls)}
              extraClass="video-player__controls-btn-icon"
              iconExtraClass="video-player__controls-btn-icon"
              size="small"
            />
          </div>

          <div className="cell auto shrink">
            <Duration
              videoDuration={videoDuration}
              played={played}
            />
          </div>

          <Seekbar
            id={id}
            seeking={seeking}
            played={played}
            playerEl={playerEl}
            setSeeking={setSeeking}
          />

          <VolumeControl
            id={id}
            muted={muted}
            muteIconSrc={muteIconSrc}
            volumeIconSrc={volumeIconSrc}
            controls={controls}
            volume={volume}
            setToggleMuted={setToggleMuted}
            setVolume={setVolume}
            title={muted ? R.prop('unmute')(controls) : R.prop('mute')(controls)}
          />
        </div>
      ) : (
        <PlayPauseControl
          id={id}
          controls={controls}
          playing={playing}
          setTogglePlayPause={setTogglePlayPause}
          pauseIconSrc={pauseIconSrc}
          playIconSrc={playIconSrc}
          title={playing ? R.prop('pause')(controls) : R.prop('play')(controls)}
          extraClass={generateSinglePlayButtonClassNames({ playButtonPosition })}
          size="medium"
          playButtonIconWidth={playButtonIconWidth}
          playButtonIconHeight={playButtonIconHeight}
        />
      )
    )}
  </Fragment>
);

Controls.propTypes = {
  id: PropTypes.string.isRequired,
  muted: PropTypes.bool,
  playing: PropTypes.bool,
  volume: PropTypes.number,
  videoDuration: PropTypes.number,
  played: PropTypes.number,
  seeking: PropTypes.bool,
  setTogglePlayPause: PropTypes.func,
  setVolume: PropTypes.func,
  setToggleMuted: PropTypes.func,
  setSeeking: PropTypes.func,
  playerEl: PropTypes.objectOf(PropTypes.any),
  controls: PropTypes.shape({
    play: PropTypes.string,
    pause: PropTypes.string,
    mute: PropTypes.string,
    unmute: PropTypes.string,
  }),
  playIconSrc: PropTypes.string,
  pauseIconSrc: PropTypes.string,
  muteIconSrc: PropTypes.string,
  volumeIconSrc: PropTypes.string,
  playButtonPosition: PropTypes.oneOf(['topLeft', 'topRight', 'bottomRight', 'bottomLeft', 'middle']),
  showOnlyMuteControls: PropTypes.string,
  playButtonIconWidth: PropTypes.number,
  playButtonIconHeight: PropTypes.number,
};

export default Controls;

